import type { CSSProperties, FC, ReactNode } from 'react'
import type { StageDrawing as Kind } from '@/constants/process'

interface StageDrawingProps {
    kind: Kind
    label: string
}

const INK = '#ECE6D8'
const MUTE = '#8A8478'
const SIGNAL = '#86B6F0'

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

interface Stroke {
    d: string
    tone?: 'ink' | 'mute' | 'signal'
    width?: number
    dash?: string
}

const colour = (tone: Stroke['tone']): string => (tone === 'signal' ? SIGNAL : tone === 'mute' ? MUTE : INK)

const iso = (x: number, y: number, z: number): string => `${(160 + (x - z) * 0.866).toFixed(1)} ${(150 + (x + z) * 0.5 - y).toFixed(1)}`

const isoBox = (x: number, z: number, w: number, d: number, h: number): string => [
    `M${iso(x, h, z)} L${iso(x + w, h, z)} L${iso(x + w, h, z + d)} L${iso(x, h, z + d)} Z`,
    `M${iso(x, h, z)} L${iso(x, 0, z)} L${iso(x + w, 0, z)} L${iso(x + w, h, z)}`,
    `M${iso(x + w, 0, z)} L${iso(x + w, 0, z + d)} L${iso(x + w, h, z + d)}`,
    `M${iso(x, 0, z)} L${iso(x, 0, z + d)} L${iso(x, h, z + d)}`
].join(' ')

const strokesFor = (kind: Kind): Array<Stroke> => {
    switch (kind) {
        case 'site': {
            const trees = [[70, 70], [96, 58], [250, 210], [272, 196], [232, 64]]
                .map(([x, y]) => `M${x - 5} ${y} h10 M${x} ${y - 5} v10`).join(' ')

            return [
                { d: 'M40 196 C70 150 60 100 110 70 S220 40 270 70 S300 170 250 220 S120 240 40 196 Z', tone: 'mute', width: 0.7 },
                { d: 'M64 190 C90 150 86 112 124 88 S214 64 254 88 S278 168 238 204 S130 222 64 190 Z', tone: 'mute', width: 0.7 },
                { d: 'M60 230 L52 90 L180 46 L292 104 L284 238 Z', dash: '6 4', width: 1.2 },
                { d: 'M126 120 h70 v60 h-70 Z', tone: 'signal', width: 1 },
                { d: trees, tone: 'mute', width: 0.8 },
                { d: 'M296 34 v26 M290 46 L296 34 L302 46', width: 1 }
            ]
        }
        case 'massing':
            return [
                { d: `M${iso(-90, 0, -60)} L${iso(110, 0, -60)} L${iso(110, 0, 80)} L${iso(-90, 0, 80)} Z`, tone: 'mute', width: 0.7 },
                { d: isoBox(-70, -30, 60, 60, 40), width: 1.1 },
                { d: isoBox(-10, -30, 60, 60, 66), width: 1.1 },
                { d: isoBox(50, -30, 40, 60, 92), tone: 'signal', width: 1.1 }
            ]
        case 'section':
            return [
                { d: 'M20 230 H300', tone: 'mute', width: 0.7 },
                { d: 'M50 230 V90 L120 90 L120 74 L190 74 L190 58 L270 58 V230', width: 1.4 },
                { d: 'M50 186 H270 M50 140 H270', width: 1 },
                { d: 'M50 230 V240 H270 V230', tone: 'mute', width: 0.7 },
                { d: 'M90 140 V230 M150 140 V230 M210 140 V230 M90 90 V140 M150 74 V140 M210 58 V140', tone: 'mute', width: 0.6, dash: '3 3' },
                { d: 'M282 230 v-4 h8 M282 186 v-4 h8 M282 140 v-4 h8 M282 58 v-4 h8', tone: 'signal', width: 0.9 }
            ]
        case 'scaffold': {
            const verticals = [60, 100, 140, 180, 220, 260].map(x => `M${x} 236 V60`).join(' ')
            const lifts = [196, 156, 116, 76].map(y => `M52 ${y} H268`).join(' ')
            const braces = 'M60 236 L100 196 L140 156 L180 116 L220 76 M260 236 L220 196 L180 156'

            return [
                { d: 'M20 236 H300', tone: 'mute', width: 0.7 },
                { d: 'M80 236 V120 H240 V236', width: 1.4 },
                { d: verticals, tone: 'mute', width: 0.8 },
                { d: lifts, tone: 'mute', width: 0.8 },
                { d: braces, tone: 'signal', width: 0.9 },
                { d: 'M150 120 V44 M150 44 H230 M226 44 v14', width: 1 }
            ]
        }
        case 'plan':
            return [
                { d: 'M40 50 H280 V230 H40 Z', width: 1.6 },
                { d: 'M40 130 H150 M190 130 H280 M150 50 V100 M150 160 V230 M220 130 V230', width: 1 },
                { d: 'M150 100 A30 30 0 0 1 180 130 M150 160 A30 30 0 0 0 120 130', tone: 'mute', width: 0.7 },
                { d: 'M232 62 h36 v56 h-36 Z M232 74 h36 M232 86 h36 M232 98 h36 M232 110 h36', tone: 'mute', width: 0.7 },
                { d: 'M60 214 h70 M60 222 v-16 M130 222 v-16', tone: 'signal', width: 0.9 }
            ]
        case 'sensors': {
            const nodes: Array<[number, number]> = [[80, 90], [160, 80], [240, 92], [92, 170], [170, 190], [250, 176], [128, 128], [214, 136]]
            const links = 'M80 90 L128 128 L160 80 M128 128 L92 170 M128 128 L170 190 L214 136 L240 92 M214 136 L250 176 M160 80 L214 136'
            const squares = nodes.map(([x, y]) => `M${x - 4} ${y - 4} h8 v8 h-8 Z`).join(' ')

            return [
                { d: 'M40 50 H280 V230 H40 Z', tone: 'mute', width: 1 },
                { d: 'M40 130 H150 M190 130 H280 M150 50 V100 M150 160 V230', tone: 'mute', width: 0.7 },
                { d: links, tone: 'signal', width: 0.8, dash: '3 3' },
                { d: squares, tone: 'signal', width: 1.1 }
            ]
        }
        default:
            return []
    }
}

/**
 * The small line drawing for one stage of a commission. Strokes draw themselves in when the
 * stage's parent switches data-reveal to "in".
 */
export const StageDrawing: FC<StageDrawingProps> = ({ kind, label }): ReactNode => {
    const strokes = strokesFor(kind)

    return (
        <svg viewBox='0 0 320 280' className='h-full w-full' role='img' aria-label={label}>
            {strokes.map((stroke, index) => (
                <path
                    key={`${kind}-${index}`}
                    d={stroke.d}
                    fill='none'
                    stroke={colour(stroke.tone)}
                    strokeWidth={stroke.width ?? 1}
                    strokeDasharray={stroke.dash}
                    strokeLinejoin='round'
                    pathLength={stroke.dash ? undefined : 1}
                    className={stroke.dash ? 'reveal-cell' : 'draw-path'}
                    style={delay(120 + index * 160)}
                />
            ))}
        </svg>
    )
}
